import { useState } from "react";
import PropTypes from "prop-types";
import ModalV2 from "../../ModalV2";
import useModal from "../../../hooks/useModal";
import { Button } from "@oc-labs/wt-storybook";
import { X, Copy, CheckCircle2, ExternalLink, Layers, Inbox } from "lucide-react";

const shortenHex = (hex) => {
  if (!hex || typeof hex !== "string") return "";
  if (hex.length <= 14) return hex;
  return `${hex.slice(0, 8)}…${hex.slice(-6)}`;
};

const formatDate = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleString();
};

const buildExplorerUrl = (base, kind, value) => {
  if (!base || !value) return null;
  const trimmed = base.endsWith("/") ? base.slice(0, -1) : base;
  return `${trimmed}/${kind}/${value}`;
};

export default function SourceDeploymentsModal() {
  const { close, data, isOpen } = useModal("source-deployments-modal");
  const [copied, setCopied] = useState(null);

  const sourceName = data?.sourceName || "";
  const deployments = Array.isArray(data?.deployments) ? data.deployments : [];

  const copy = (key, value) => {
    if (value == null) return;
    navigator.clipboard.writeText(String(value));
    setCopied(key);
    setTimeout(() => setCopied(null), 1500);
  };

  if (!isOpen) return null;

  return (
    <ModalV2 name="source-deployments-modal" className="w-[min(820px,95vw)] max-h-[90vh]">
      <div className="flex flex-col max-h-[85vh] w-full">
        <div className="shrink-0 p-6 border-b border-gray-300 bg-gradient-to-br from-blue-600 to-indigo-700 rounded-xl">
          <div className="flex justify-between items-start">
            <div className="flex flex-col gap-1">
              <h1 className="text-white text-2xl font-black flex items-center gap-2">
                <Layers size={22} /> Deployments
              </h1>
              <p className="text-blue-100 text-sm">
                On-chain deployments of <span className="font-mono">{sourceName}</span>.
              </p>
            </div>
            <button className="text-gray-200 hover:text-white" onClick={() => close()}>
              <X />
            </button>
          </div>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto px-6 py-5 space-y-4">
          {deployments.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-2 py-12 text-gray-500">
              <Inbox size={32} className="text-gray-400" />
              <p className="text-sm">This contract has not been deployed yet.</p>
            </div>
          ) : (
            deployments.map((d) => {
              const txHash = d.txHash || d.transactionHash;
              const explorer = d.network?.explorerUrl;
              const addressUrl = buildExplorerUrl(explorer, "address", d.address);
              const txUrl = buildExplorerUrl(explorer, "tx", txHash);
              return (
                <div
                  key={d.id || `${d.address}-${d.chainId}`}
                  className="rounded-lg border border-gray-200 bg-white shadow-sm"
                >
                  <div className="flex items-center justify-between gap-2 px-4 py-3 border-b border-gray-100 bg-gray-50 rounded-t-lg">
                    <div className="flex items-center gap-2 min-w-0">
                      {d.network?.symbol && (
                        <span className="shrink-0 inline-flex items-center rounded-full bg-blue-100 text-blue-800 text-xs font-semibold px-2 py-0.5">
                          {d.network.symbol}
                        </span>
                      )}
                      <span className="text-sm font-medium text-gray-900 truncate">
                        {d.network?.name || "Unknown network"}
                      </span>
                      {d.chainId != null && (
                        <span className="text-xs text-gray-500">chainId {d.chainId}</span>
                      )}
                    </div>
                    <span className="text-xs text-gray-500 shrink-0">{formatDate(d.createdAt)}</span>
                  </div>

                  <div className="divide-y divide-gray-100">
                    <Row
                      label="Address"
                      value={d.address}
                      copyKey={`addr-${d.id}`}
                      copied={copied}
                      onCopy={copy}
                      href={addressUrl}
                    />
                    <Row
                      label="Tx hash"
                      value={txHash}
                      copyKey={`tx-${d.id}`}
                      copied={copied}
                      onCopy={copy}
                      href={txUrl}
                    />
                    <div className="px-4 py-3">
                      <p className="text-xs uppercase tracking-wider text-gray-500 mb-1">
                        Constructor arguments
                      </p>
                      {Array.isArray(d.constructorArguments) && d.constructorArguments.length > 0 ? (
                        <ol className="list-decimal list-inside text-sm font-mono text-gray-900 space-y-0.5">
                          {d.constructorArguments.map((v, i) => (
                            <li key={i} className="break-all">
                              {typeof v === "string" ? `"${v}"` : String(v)}
                            </li>
                          ))}
                        </ol>
                      ) : (
                        <p className="text-xs text-gray-500 italic">None</p>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        <div className="shrink-0 bg-white border-t border-gray-200 px-6 py-4 flex justify-end gap-3">
          <Button type="primary" onClick={() => close()}>
            Close
          </Button>
        </div>
      </div>
    </ModalV2>
  );
}

function Row({ label, value, copyKey, copied, onCopy, href }) {
  if (!value) return null;
  return (
    <div className="px-4 py-3 flex items-center justify-between gap-3">
      <div className="flex flex-col min-w-0">
        <span className="text-xs uppercase tracking-wider text-gray-500">{label}</span>
        <span className="text-sm font-mono text-gray-900 break-all" title={value}>
          <span className="hidden sm:inline">{value}</span>
          <span className="sm:hidden">{shortenHex(value)}</span>
        </span>
      </div>
      <div className="shrink-0 flex items-center gap-1">
        {href && (
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="p-2 text-gray-400 hover:text-blue-600 transition"
            aria-label="Open in explorer"
          >
            <ExternalLink size={16} />
          </a>
        )}
        <button
          onClick={() => onCopy(copyKey, value)}
          className="p-2 text-gray-400 hover:text-blue-600 transition"
          aria-label="Copy"
        >
          {copied === copyKey ? (
            <CheckCircle2 size={16} className="text-green-600" />
          ) : (
            <Copy size={16} />
          )}
        </button>
      </div>
    </div>
  );
}

Row.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string,
  copyKey: PropTypes.string.isRequired,
  copied: PropTypes.string,
  onCopy: PropTypes.func.isRequired,
  href: PropTypes.string,
};
